import { useState, type FormEvent } from 'react';
import { darkTheme } from '../../lib/tokens';
import { ModalShell, ModalActions } from '../../components/PromptDialog';
import type { SubscriptionTier } from './types';

// Tier-Override als echtes Formular-Modal statt der früheren window.prompt-Kette
// (drei prompts hintereinander). Die nativen Dialoge werden auf iOS-Safari
// unterdrückt — der „Anpassen"-Button sah dort aus, als würde er nichts tun.

export const TIER_OPTIONS: { value: SubscriptionTier; label: string }[] = [
  { value: 'FREE', label: 'Free' },
  { value: 'PREMIUM_MONTHLY', label: 'Premium — monatlich' },
  { value: 'PREMIUM_YEARLY', label: 'Premium — jährlich' },
  { value: 'PREMIUM_PLUS_MONTHLY', label: 'Premium Plus — monatlich' },
  { value: 'PREMIUM_PLUS_YEARLY', label: 'Premium Plus — jährlich' },
];

export interface SubscriptionOverrideResult {
  tier: SubscriptionTier;
  expiresAt: string | null;
  reason: string;
}

export function SubscriptionOverrideModal({
  appName,
  currentTier,
  onSubmit,
  onCancel,
}: {
  appName: string;
  currentTier: SubscriptionTier;
  onSubmit: (result: SubscriptionOverrideResult) => void;
  onCancel: () => void;
}): JSX.Element {
  const [tier, setTier] = useState<SubscriptionTier>(currentTier);
  // <input type="date"> liefert 'YYYY-MM-DD'; leer = unbegrenzt.
  const [expires, setExpires] = useState('');
  const [reason, setReason] = useState('');

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!reason.trim()) return;
    let expiresAt: string | null = null;
    if (tier !== 'FREE' && expires) {
      const d = new Date(`${expires}T23:59:59`);
      if (Number.isNaN(d.getTime())) return;
      expiresAt = d.toISOString();
    }
    onSubmit({ tier, expiresAt, reason: reason.trim() });
  }

  const inputStyle = { borderColor: darkTheme.colors.borderSubtle };
  const inputClass =
    'w-full rounded-md border bg-bg-base px-3 py-2 text-sm text-text-primary outline-none focus:border-accent';

  return (
    <ModalShell title={`Subscription anpassen — ${appName}`} onSubmit={handleSubmit}>
      <p className="mb-4 text-sm text-text-secondary">
        Aktuell: <span className="font-semibold text-text-primary">{tierLabel(currentTier)}</span>
      </p>

      <div className="space-y-3">
        <label className="block">
          <span className="mb-1 block text-xs font-medium text-text-secondary">Neuer Tier</span>
          <select
            value={tier}
            onChange={(e) => setTier(e.target.value as SubscriptionTier)}
            className={inputClass}
            style={inputStyle}
          >
            {TIER_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </label>

        {tier !== 'FREE' && (
          <label className="block">
            <span className="mb-1 block text-xs font-medium text-text-secondary">
              Läuft ab (leer = unbegrenzt)
            </span>
            <input
              type="date"
              value={expires}
              onChange={(e) => setExpires(e.target.value)}
              className={inputClass}
              style={inputStyle}
            />
          </label>
        )}

        <label className="block">
          <span className="mb-1 block text-xs font-medium text-text-secondary">
            Begründung (Pflicht)
          </span>
          <textarea
            required
            rows={3}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="z. B. Support-Kulanz, Ticket #123"
            className={inputClass}
            style={inputStyle}
          />
        </label>
      </div>

      <ModalActions onCancel={onCancel} confirmLabel="Weiter" disabled={!reason.trim()} />
    </ModalShell>
  );
}

export function tierLabel(tier: SubscriptionTier): string {
  return TIER_OPTIONS.find((o) => o.value === tier)?.label ?? tier;
}
